import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Box, Heading, Spinner, Alert, AlertIcon } from '@chakra-ui/react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-grid.css';
import { useToast } from '@chakra-ui/react';

const Marksheet = () => {
  const { userId } = useParams();
  const [marksheetData, setMarksheetData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();

  useEffect(() => {
    const fetchMarksheet = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Results/marksheet/${userId}`);
        const formattedData = response.data.$values.map((item) => ({
          ...item,
          ...item.examDetails.$values[0],
          refid: item.refid,
          name: item.username,
          empcode: item.userId,
          paperSetTitle: item.paperSetTitle,
          examDate: item.examDate,
          isCompleted: item.isCompleted,
          submissionDate: item.submissionDate,
        }));
        setMarksheetData(formattedData);
      } catch (error) {
        setError('Error fetching marksheet');
        console.error('Error fetching marksheet:', error);
        toast({
          title: "Error fetching marksheet",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMarksheet();
  }, [userId, toast]);

  const handleCellValueChanged = async (params) => {
    const updatedData = {
      examAssignmentId: params.data.examAssignmentId,
      responseAcceptable: params.data.responseAcceptable,
      remarks: params.data.remarks,
    };

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/Results/update`, updatedData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      toast({
        title: "Success",
        description: "Marksheet updated successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating marksheet:', error);
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const columnDefs = [
    { headerName: 'Ref ID', field: 'refid' },
    { headerName: 'Name', field: 'name' },
    { headerName: 'Emp Code', field: 'empcode' },
    { headerName: 'DOJ', field: 'doj', valueFormatter: ({ value }) => new Date(value).toLocaleDateString() },
    { headerName: 'Designation', field: 'designation' },
    { headerName: 'Office', field: 'office' },
    { headerName: 'City', field: 'city' },
    { headerName: 'Email', field: 'email' },
    { headerName: 'Test Set', field: 'paperSetTitle' },
    { headerName: 'Exam Date', field: 'examDate', valueFormatter: ({ value }) => new Date(value).toLocaleDateString() },
    { headerName: 'Is Completed', field: 'isCompleted', valueFormatter: ({ value }) => (value ? 'Yes' : 'No') },
    { headerName: 'Submission Date', field: 'submissionDate', valueFormatter: ({ value }) => value ? new Date(value).toLocaleDateString() : 'N/A' },
    { headerName: 'Correct Answers', field: 'correctAnswers' },
    { headerName: 'Wrong Answers', field: 'wrongAnswers' },
    { headerName: 'Correct Answer Marks', field: 'correctAnswerMarks' },
    { headerName: 'Negative Marks', field: 'negativeMarks' },
    { headerName: 'Total Marks', field: 'totalMarks' },
    { headerName: 'Not Attempted Questions', field: 'notAttemptedQuestions' },
    { headerName: 'Response Acceptable', field: 'responseAcceptable', editable: true, cellEditor: 'agSelectCellEditor', cellEditorParams: { values: ['Yes', 'No'] } },
    { headerName: 'Remarks', field: 'remarks', editable: true },
  ];

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Heading mb={4}>Marksheet</Heading>
      <div className="ag-theme-alpine" style={{ height: 600, width: '100%' }}>
        <AgGridReact
          rowData={marksheetData}
          columnDefs={columnDefs}
          onCellValueChanged={handleCellValueChanged}
          pagination={true}
          paginationPageSize={10}
        />
      </div>
    </Box>
  );
};

export default Marksheet;